import * as React from "react"

import { cn } from "@/lib/utils"

import { REPORT_TABS, type ReportTabId } from "./report-tab-ids"

function tabPanelId(id: ReportTabId): string {
  return `report-panel-${id}`
}

function tabId(id: ReportTabId): string {
  return `report-tab-${id}`
}

/**
 * The report's four views as an ARIA tablist: arrow keys move between tabs,
 * Home and End jump to the ends, and only the selected tab is in the tab order.
 */
export function ReportTabs({
  value,
  onChange,
}: {
  value: ReportTabId
  onChange: (next: ReportTabId) => void
}): JSX.Element {
  const refs = React.useRef(new Map<ReportTabId, HTMLButtonElement>())

  const focusAt = (index: number): void => {
    const count = REPORT_TABS.length
    const next = REPORT_TABS[((index % count) + count) % count]
    if (!next) return
    onChange(next.id)
    refs.current.get(next.id)?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label="Report views"
      className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-surface-sunken p-1"
    >
      {REPORT_TABS.map((tab, i) => {
        const selected = tab.id === value
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) refs.current.set(tab.id, el)
              else refs.current.delete(tab.id)
            }}
            id={tabId(tab.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={tabPanelId(tab.id)}
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              onChange(tab.id)
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") focusAt(i + 1)
              else if (event.key === "ArrowLeft") focusAt(i - 1)
              else if (event.key === "Home") focusAt(0)
              else if (event.key === "End") focusAt(REPORT_TABS.length - 1)
              else return
              event.preventDefault()
            }}
            className={cn(
              "rounded-md px-3 py-1.5 text-body-sm font-medium transition-colors duration-120",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
              selected ? "bg-surface text-text shadow-e1" : "text-text-3 hover:text-text",
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

/** The panel a tab controls. */
export function ReportTabPanel({
  id,
  children,
}: {
  id: ReportTabId
  children: React.ReactNode
}): JSX.Element {
  return (
    <div
      role="tabpanel"
      id={tabPanelId(id)}
      aria-labelledby={tabId(id)}
      tabIndex={0}
      className="mt-8 focus-visible:outline-none"
    >
      {children}
    </div>
  )
}
